/**
 * The document store.
 *
 * Saves to the server, with localStorage as a local cache rather than the source of truth.
 * That ordering matters: localStorage was the only persistence for a while, and a cleared
 * cache lost the work. It is kept because it makes the editor usable offline and survives a
 * server restart mid-edit — but the server is what is trusted on load.
 */

import { create } from 'zustand';
import type { CollctivDoc, DocType, PageMeta, PuckDataSnapshot } from '@lp/shared';
import { emptyMeta } from '@lp/shared';
import { migratePuckData } from '@lp/blocks';
import { api, serverAvailable, type DocSummary } from '../api/client.js';

const CACHE_KEY = 'lpbuilder.doc.v1';
const AUTOSAVE_MS = 1500;

export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'offline' | 'error';

interface DocState {
  id: string;
  title: string;
  docType: DocType;
  meta: PageMeta;
  puck: PuckDataSnapshot;

  saveState: SaveState;
  lastSavedAt: string | null;
  saveError: string | null;
  /** False until the first load settles, so nothing autosaves over a document still loading. */
  ready: boolean;

  setTitle(title: string): void;
  setMeta(patch: Partial<PageMeta>): void;
  setPuck(data: PuckDataSnapshot): void;

  toDoc(): CollctivDoc;
  load(): Promise<void>;
  saveNow(label?: string): Promise<void>;
  replaceDoc(doc: CollctivDoc): void;
  reset(docType: DocType): void;

  /* The page library. Storage has been addressed by id from the start, so these are thin. */

  /** Every saved page, newest first. */
  pages: DocSummary[];
  pagesLoading: boolean;
  refreshPages(): Promise<void>;
  /** Save what is open, then switch to another page. */
  openPage(id: string): Promise<void>;
  /** Save what is open, then start a blank one. */
  newPage(docType: DocType): Promise<void>;
  /** Copy the open page under a new id, and switch to the copy. */
  duplicatePage(): Promise<void>;
  deletePage(id: string): Promise<void>;
}

const emptyPuck: PuckDataSnapshot = { root: { props: {} }, content: [] };
const newId = (): string => `doc-${Date.now().toString(36)}`;

function readCache(): CollctivDoc | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as CollctivDoc) : null;
  } catch {
    return null;
  }
}

function writeCache(doc: CollctivDoc): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(doc));
  } catch {
    // Quota or private mode. Losing the cache is bad; crashing the editor is worse.
  }
}

let saveTimer: ReturnType<typeof setTimeout> | undefined;
let online = true;

export const useDocStore = create<DocState>((set, get) => {
  /** Debounced: typing a headline should not be one request per keystroke. */
  const queueSave = (): void => {
    if (!get().ready) return;
    set({ saveState: 'dirty' });
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => void get().saveNow(), AUTOSAVE_MS);
  };

  const touch = (): void => {
    writeCache(get().toDoc());
    queueSave();
  };

  return {
    id: newId(),
    title: 'Untitled page',
    docType: 'landing',
    meta: emptyMeta('landing'),
    puck: emptyPuck,

    saveState: 'idle',
    lastSavedAt: null,
    saveError: null,
    ready: false,

    setTitle(title) {
      set({ title });
      touch();
    },

    setMeta(patch) {
      set((s) => ({ meta: { ...s.meta, ...patch } }));
      touch();
    },

    setPuck(puck) {
      set({ puck });
      touch();
    },

    toDoc() {
      const s = get();
      const now = new Date().toISOString();
      return {
        schemaVersion: 1,
        id: s.id,
        title: s.title,
        docType: s.docType,
        meta: s.meta,
        puck: s.puck,
        // Images live in the blocks that use them, so a duplicate is a real copy rather than
        // two references to one record. Kept for schema compatibility.
        assets: {},
        createdAt: now,
        updatedAt: now,
      };
    },

    /**
     * Load the most recent page.
     *
     * Server first. If there is nothing on it but something cached locally, that cache is
     * pushed up rather than discarded — otherwise the first deploy would throw away whatever
     * was being worked on.
     */
    async load() {
      const cached = readCache();
      if (cached) {
        set({
          id: cached.id,
          title: cached.title,
          docType: cached.docType,
          meta: { ...emptyMeta(cached.docType), ...cached.meta },
          puck: migratePuckData(cached.puck ?? emptyPuck),
        });
      }

      online = await serverAvailable();
      if (!online) {
        set({ ready: true, saveState: 'offline' });
        return;
      }

      try {
        const docs = await api.listDocs();
        if (docs.length > 0) {
          const newest = docs[0]!;
          // Prefer the cache only when it is the same page — a stale cache of a different
          // page would otherwise silently replace what is on the server.
          if (!cached || cached.id === newest.id) {
            const doc = await api.getDoc(newest.id);
            set({
              id: doc.id,
              title: doc.title,
              docType: doc.docType,
              meta: { ...emptyMeta(doc.docType), ...doc.meta },
              puck: migratePuckData(doc.puck ?? emptyPuck),
              lastSavedAt: newest.updatedAt,
            });
          }
        }
        set({ ready: true, saveState: 'saved', pages: docs });

        // Nothing on the server yet but work in the cache: put it there.
        if (docs.length === 0 && cached) await get().saveNow('first sync');
        await get().refreshPages();
      } catch (err) {
        set({
          ready: true,
          saveState: 'error',
          saveError: err instanceof Error ? err.message : 'could not reach the server',
        });
      }
    },

    async saveNow(label) {
      clearTimeout(saveTimer);
      const doc = get().toDoc();
      writeCache(doc);

      if (!online) {
        set({ saveState: 'offline' });
        return;
      }

      set({ saveState: 'saving', saveError: null });
      try {
        const saved = await api.saveDoc(doc, label);
        set({ saveState: 'saved', lastSavedAt: saved.updatedAt });
      } catch (err) {
        set({
          saveState: 'error',
          saveError: err instanceof Error ? err.message : 'save failed',
        });
      }
    },

    /** Used by import and by restoring a version. */
    replaceDoc(doc) {
      set({
        id: doc.id,
        title: doc.title,
        docType: doc.docType,
        meta: { ...emptyMeta(doc.docType), ...doc.meta },
        puck: migratePuckData(doc.puck ?? emptyPuck),
      });
      touch();
    },

    reset(docType) {
      set({
        id: newId(),
        title: 'Untitled page',
        docType,
        meta: emptyMeta(docType),
        puck: emptyPuck,
      });
      touch();
    },

    /* ---------------------------------------------------------------- *
     * The page library
     * ---------------------------------------------------------------- */

    pages: [],
    pagesLoading: false,

    async refreshPages() {
      if (!online) {
        set({ pages: [], pagesLoading: false });
        return;
      }
      set({ pagesLoading: true });
      try {
        set({ pages: await api.listDocs(), pagesLoading: false });
      } catch {
        // A failed listing is not worth an error banner over the canvas; the panel says so.
        set({ pages: [], pagesLoading: false });
      }
    },

    /**
     * Switching pages always flushes first.
     *
     * Autosave is debounced, so up to 1.5s of edits are in memory and nowhere else at any
     * moment. Switching without flushing would discard exactly the last thing someone typed,
     * which is the least forgivable moment to lose work.
     */
    async openPage(id) {
      if (id === get().id) return;
      await get().saveNow();
      const doc = await api.getDoc(id);
      set({
        id: doc.id,
        title: doc.title,
        docType: doc.docType,
        meta: { ...emptyMeta(doc.docType), ...doc.meta },
        puck: migratePuckData(doc.puck ?? emptyPuck),
        saveState: 'saved',
      });
      writeCache(get().toDoc());
      await get().refreshPages();
    },

    async newPage(docType) {
      await get().saveNow();
      get().reset(docType);
      await get().saveNow('created');
      await get().refreshPages();
    },

    async duplicatePage() {
      await get().saveNow();
      const source = get().toDoc();
      set({ id: newId(), title: `${source.title} copy` });
      await get().saveNow('duplicated');
      await get().refreshPages();
    },

    async deletePage(id) {
      await api.deleteDoc(id);
      // Deleting the page you are looking at leaves the editor pointing at nothing, so open
      // the next one — or start fresh if that was the last.
      if (id === get().id) {
        const rest = (await api.listDocs()).filter((p) => p.id !== id);
        if (rest[0]) {
          const doc = await api.getDoc(rest[0].id);
          set({
            id: doc.id,
            title: doc.title,
            docType: doc.docType,
            meta: { ...emptyMeta(doc.docType), ...doc.meta },
            puck: migratePuckData(doc.puck ?? emptyPuck),
            saveState: 'saved',
          });
          writeCache(get().toDoc());
        } else {
          get().reset(get().docType);
        }
      }
      await get().refreshPages();
    },
  };
});

/** Don't lose the last few seconds of work to a closed tab. */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    const s = useDocStore.getState();
    if (s.saveState === 'dirty') writeCache(s.toDoc());
  });
}
